 import * as React from "react";
import { gql, useMutation, ApolloError } from "@apollo/client";
import {
  CreateOfferMutationMutation,
  CreateOfferMutationMutationVariables,
} from "../../../../schemaTypes";
import { normalizeErrors } from "../../../../utils/normalizeErrors";
import { NormalizedErrorMap } from "../../../../types/NormalizedErrorMap";

export const createOfferMutation = gql`
  mutation CreateOfferMutation(
    $title: String!
    $description: String!
    $bannerUrl: String
    $categoryId: String!
    $offerType: String!
    $discountType: String
    $discountValue: Int
    $amount: Float
    $njoyCodeId: String
    $partnerVoucher: [String]
    $referralUrl: String
    $status: String!
    $usageLimitPerUser: Int!
    $totalUsageLimit: Int
    $startDate: String!
    $endDate: String
    $communityId: [String!]
    $locationId: [String!]
    $offerTOR: String!
    $merchantId: String
  ) {
    createOffer(
      title: $title
      description: $description
      bannerUrl: $bannerUrl
      categoryId: $categoryId
      offerType: $offerType
      discountType: $discountType
      discountValue: $discountValue
      amount: $amount
      njoyCodeId: $njoyCodeId
      partnerVoucher: $partnerVoucher
      referralUrl: $referralUrl
      status: $status
      usageLimitPerUser: $usageLimitPerUser
      totalUsageLimit: $totalUsageLimit
      startDate: $startDate
      endDate: $endDate
      communityId: $communityId
      locationId: $locationId
      offerTOR: $offerTOR
      merchantId: $merchantId
    ) {
      errors {
        path
        message
      }
      message
      bannerUrl
    }
  }
`;


interface Props {
  onMessage?: (message: string) => void;
  children: (
    data: {
      submit: (
        values: CreateOfferMutationMutationVariables & {
          bannerFile?: File;
        }
      ) => Promise<{
        errors: NormalizedErrorMap | null;
        message?: string;
      }>;
      loading: boolean;
      error?: ApolloError;
    }
  ) => React.ReactElement | null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const CreateOffer: React.FC<Props> = ({ children, onMessage }) => {
  const [_createOfferMutation, { loading, error }] = useMutation<
    CreateOfferMutationMutation,
    CreateOfferMutationMutationVariables
  >(createOfferMutation);

  const submit = async (
    values: CreateOfferMutationMutationVariables & { bannerFile?: File }
  ) => {
    const { bannerFile, ...rest } = values;
    let bannerUrl = rest.bannerUrl;
    if (bannerFile) {
      bannerUrl = await fileToBase64(bannerFile);
    }

    // Directly send all values to backend, let backend handle all business logic and validation
    const { data } = await _createOfferMutation({
      variables: {
        ...rest,
        bannerUrl,
      },
    });

    const errors = data?.createOffer?.errors
      ? normalizeErrors(data.createOffer.errors)
      : null;

    if (data?.createOffer?.message && onMessage) {
      onMessage(data.createOffer.message);
    }

    return {
      errors,
      message: data?.createOffer?.message ?? undefined,
      bannerUrl: data?.createOffer?.bannerUrl ?? undefined,
    };
  };

  return children({ submit, loading, error });
};